const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;
const UTC = 'UTC';
const ABBREVIATION_LOCALES = ['en-US', 'en-GB', 'en-IN', 'en-AU'];
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MIN_YEAR = 1000;
const TIME_RE = /^(\d{2}):(\d{2})$/;

export interface StoreWallTime {
  date: string;
  time: string;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

const zoneValidity = new Map<string, boolean>();

function isValidZone(tz: string): boolean {
  const cached = zoneValidity.get(tz);
  if (cached !== undefined) return cached;
  let valid = true;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
  } catch {
    valid = false;
  }
  zoneValidity.set(tz, valid);
  return valid;
}

/** The zone every conversion runs in: the store's own, or UTC when it is unknown. */
function resolveZone(tz?: string): string {
  return tz && isValidZone(tz) ? tz : UTC;
}

/** Shown wherever the zone is named: the IANA id, or "UTC" when the store's is unknown. */
export function storeZoneLabel(tz?: string): string {
  return resolveZone(tz);
}

function wallFormatter(zone: string): Intl.DateTimeFormat {
  let f = formatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(zone, f);
  }
  return f;
}

/** The zone's wall clock at `ms`, read as if it were UTC. */
function wallAsUtcMs(ms: number, zone: string): number {
  const parts = wallFormatter(zone).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
}

const offsetMs = (ms: number, zone: string): number =>
  wallAsUtcMs(ms, zone) - Math.floor(ms / 1000) * 1000;

const pad = (n: number): string => String(n).padStart(2, '0');

function wallToUtcMs(date: string, time: string): number | undefined {
  const d = DATE_RE.exec(date);
  const t = TIME_RE.exec(time);
  if (!d || !t) return undefined;
  const [year, month, day, hour, minute] = [d[1], d[2], d[3], t[1], t[2]].map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  // Date.UTC maps years 0-99 to 19xx and rolls impossible dates over, so a
  // half-typed year or 2026-02-30 would silently become a different date.
  if (year < MIN_YEAR) return undefined;
  const ms = Date.UTC(year, month - 1, day, hour, minute);
  const back = new Date(ms);
  const same =
    back.getUTCFullYear() === year &&
    back.getUTCMonth() === month - 1 &&
    back.getUTCDate() === day &&
    back.getUTCHours() === hour &&
    back.getUTCMinutes() === minute;
  return same ? ms : undefined;
}

/** First minute at or after `fromMs` whose zone offset is `offset`, searching up to `toMs`. */
function firstMinuteWithOffset(fromMs: number, toMs: number, offset: number, zone: string): number {
  let lo = Math.floor(fromMs / MINUTE_MS);
  let hi = Math.floor(toMs / MINUTE_MS);
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (offsetMs(mid * MINUTE_MS, zone) === offset) hi = mid;
    else lo = mid + 1;
  }
  return lo * MINUTE_MS;
}

/**
 * Store wall time to an ISO instant. A wall time that DST skips resolves to the
 * first valid minute after the gap; one that DST repeats resolves to the earlier
 * instant, unless `prefer` holds an instant that already shows this wall time
 * (so touching a value and putting it back never shifts it by an hour). Returns
 * undefined for a partial or impossible date or time.
 */
export function fromStoreWallTime(
  date: string,
  time: string,
  tz?: string,
  prefer: readonly (string | undefined)[] = [],
): string | undefined {
  const wall = wallToUtcMs(date, time);
  if (wall === undefined) return undefined;
  const zone = resolveZone(tz);
  for (const known of prefer) {
    const shown = known ? toStoreWallTime(known, zone) : undefined;
    if (known && shown?.date === date && shown.time === time) return known;
  }
  const before = offsetMs(wall - DAY_MS, zone);
  const after = offsetMs(wall + DAY_MS, zone);
  const candidates = [before, after]
    .map((off) => wall - off)
    .filter((ms) => wallAsUtcMs(ms, zone) === wall);
  if (candidates.length > 0) return new Date(Math.min(...candidates)).toISOString();
  const start = firstMinuteWithOffset(
    wall - Math.max(before, after),
    wall - Math.min(before, after),
    after,
    zone,
  );
  return new Date(start).toISOString();
}

export function toStoreWallTime(iso: string | undefined, tz?: string): StoreWallTime {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return { date: '', time: '' };
  const w = new Date(wallAsUtcMs(ms, resolveZone(tz)));
  return {
    date: `${w.getUTCFullYear()}-${pad(w.getUTCMonth() + 1)}-${pad(w.getUTCDate())}`,
    time: `${pad(w.getUTCHours())}:${pad(w.getUTCMinutes())}`,
  };
}

/** Today's calendar date in the store zone, `yyyy-mm-dd`. */
export function storeToday(tz?: string, nowMs: number = Date.now()): string {
  return toStoreWallTime(new Date(nowMs).toISOString(), tz).date;
}

const toMinute = (ms: number): string =>
  new Date(Math.floor(ms / MINUTE_MS) * MINUTE_MS).toISOString();

/** A start picked by date only: now when the date is today in the store zone, else 00:00 there. */
export function defaultStartForDate(
  date: string,
  tz?: string,
  nowMs: number = Date.now(),
): string | undefined {
  if (date === storeToday(tz, nowMs)) return toMinute(nowMs);
  return fromStoreWallTime(date, '00:00', tz);
}

/** An end picked by date only: 23:59 store time that day. */
export function defaultEndForDate(date: string, tz?: string): string | undefined {
  return fromStoreWallTime(date, '23:59', tz);
}

const cachedFormatters = new Map<string, Intl.DateTimeFormat>();

function cachedFormatter(
  locale: string,
  zone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${zone}|${JSON.stringify(options)}`;
  let f = cachedFormatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { timeZone: zone, ...options });
    cachedFormatters.set(key, f);
  }
  return f;
}

function zoneAbbreviation(ms: number, zone: string): string {
  let fallback = '';
  for (const locale of ABBREVIATION_LOCALES) {
    const name = cachedFormatter(locale, zone, { timeZoneName: 'short' })
      .formatToParts(new Date(ms))
      .find((p) => p.type === 'timeZoneName')?.value;
    if (!name) continue;
    if (!/^GMT[+-]/.test(name)) return name;
    fallback ||= name;
  }
  return fallback || zone;
}

/** The zone's short name at an instant ("EDT", "IST"); "UTC" for an unknown zone. */
export function storeZoneAbbreviation(iso: string | undefined, tz?: string): string {
  const ms = iso ? Date.parse(iso) : NaN;
  const zone = resolveZone(tz);
  return zoneAbbreviation(Number.isNaN(ms) ? Date.now() : ms, zone);
}

/** "13 Oct 2026, 6:30 PM EDT" — an instant in the store zone, with its abbreviation. */
export function fmtStoreDateTime(iso: string | undefined, tz?: string): string {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return '';
  const zone = resolveZone(tz);
  const when = new Date(ms);
  const day = cachedFormatter('en-GB', zone, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(when);
  const clock = cachedFormatter('en-US', zone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(when);
  return `${day}, ${clock} ${zoneAbbreviation(ms, zone)}`;
}

/** "13 Oct 2026" — the day of an instant in the store zone, no clock or abbreviation. */
export function fmtStoreDate(iso: string | undefined, tz?: string): string {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return '';
  return cachedFormatter('en-GB', resolveZone(tz), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(ms));
}
